/**
 * Profile.js — agent profile + settings.
 *
 * Shows logged-in agent info, sound toggle, link to dashboard, sign out.
 */

import { html } from 'htm/preact';
import { useState, useEffect } from 'preact/hooks';
import { useStore } from '@nanostores/preact';

import { $session, clearSession, updateSessionChannels } from '../stores/session.js';
import { navigate, showToast } from '../stores/ui.js';
import { SCREENS, BUILD_VERSION } from '../shared/constants.js';
import { disconnectRealtime } from '../services/realtime.js';
import { unregisterPush } from '../services/push.js';
import { $canInstall, $isInstalled, promptInstall, dismissInstallCta } from '../services/pwa-install.js';
import * as api from '../services/api.js';

export function Profile() {
  const session     = useStore($session);
  const canInstall  = useStore($canInstall);
  const isInstalled = useStore($isInstalled);
  const [installing, setInstalling] = useState(false);
  const [updating, setUpdating]     = useState(false);
  // Inbox 9.3 — Refresh Channels (available to every user_type)
  const [refreshingChannels, setRefreshingChannels] = useState(false);
  // Service worker status — "active" when the page is controlled by an SW,
  // "updating" while a new one is installing, "off" if SW unsupported.
  // Used in the version footer so agents can self-report when something
  // looks stale. S4: the in-tab stale-JS comparison has been removed —
  // the SW's activate handler force-navigates open windows on update, so
  // the tab never legitimately drifts from the deployed version.
  const [swStatus, setSwStatus] = useState('checking');
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      setSwStatus('off');
      return;
    }
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) { setSwStatus('off'); return; }
      if (reg.installing || reg.waiting) { setSwStatus('updating'); return; }
      setSwStatus(reg.active ? 'active' : 'off');
    }).catch(() => setSwStatus('off'));
  }, []);

  const handleLogout = async () => {
    console.log('[v2 profile] logout flow starting');
    try { await unregisterPush(); } catch {}
    try { await api.logout(); } catch {}
    disconnectRealtime();
    clearSession();
    console.log('[v2 profile] ✓ logged out');
    navigate('login');
  };

  // Force the service worker to pull a fresh sw.js + bundle, then reload
  // the page so the installed PWA picks up the latest deploy without the
  // agent having to uninstall/reinstall or wait for Chrome's 24h check.
  //
  // Flow:
  //   1. reg.update()          — bypass HTTP cache, fetch sw.js head
  //   2. if reg.waiting        — skip-waiting → SW activates → its activate
  //                              handler force-navigates this client
  //   3. else                  — short wait for updatefound (handled in
  //                              app.js); if nothing new, just hard-reload
  //                              to bust any stale tab-local state
  //
  // No SW support → straight hard-reload via location.reload(true) shim
  // (Chrome still respects the SW bypass headers on a reload).
  const handleUpdate = async () => {
    if (updating) return;
    setUpdating(true);
    showToast('Checking for updates…', 1500);
    try {
      if (!('serviceWorker' in navigator)) {
        location.reload();
        return;
      }
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) {
        location.reload();
        return;
      }
      await reg.update().catch(() => {});

      // Give updatefound up to 2.5s to fire. If a new SW is now installing
      // or waiting, ask it to skip-waiting; activate handler in sw.js will
      // call client.navigate() on us. Else just reload.
      const waitedReg = await new Promise((resolve) => {
        if (reg.waiting || reg.installing) return resolve(reg);
        let resolved = false;
        const onFound = () => {
          if (resolved) return;
          resolved = true;
          reg.removeEventListener('updatefound', onFound);
          resolve(reg);
        };
        reg.addEventListener('updatefound', onFound);
        setTimeout(() => {
          if (resolved) return;
          resolved = true;
          reg.removeEventListener('updatefound', onFound);
          resolve(reg);
        }, 2500);
      });

      const candidate = waitedReg.waiting || waitedReg.installing;
      if (candidate) {
        candidate.postMessage({ type: 'skip-waiting' });
        // Safety: if the SW doesn't navigate us within 3s, force-reload.
        setTimeout(() => { try { location.reload(); } catch {} }, 3000);
      } else {
        showToast('Already up to date', 1400);
        setTimeout(() => { try { location.reload(); } catch {} }, 600);
      }
    } catch (e) {
      showToast(`Update failed: ${e?.message || 'unknown'}`, 2200);
      setUpdating(false);
    }
  };

  // Inbox 9.3 — pull fresh channel list from Partner Panel via inbox-auth.
  // Open to every user_type (owner + team member). Toast on result.
  const handleRefreshChannels = async () => {
    if (refreshingChannels) return;
    setRefreshingChannels(true);
    try {
      const res = await api.refreshChannels();
      const channels = Array.isArray(res?.channels) ? res.channels : [];
      updateSessionChannels(channels);
      showToast('Channels refreshed', 1400);
    } catch (e) {
      showToast('Refresh failed', 1600);
    } finally {
      setRefreshingChannels(false);
    }
  };

  // #188 — fire the cached beforeinstallprompt. Toast either way so the
  // tap is acknowledged.
  const handleInstall = async () => {
    if (installing) return;
    setInstalling(true);
    try {
      const outcome = await promptInstall();
      if (outcome === 'accepted')      showToast('Installing ODIV Inbox…', 2000);
      else if (outcome === 'dismissed') showToast('Install cancelled', 1600);
    } finally {
      setInstalling(false);
    }
  };

  const initials = (session?.agentName || session?.agentEmail || 'U').slice(0, 2).toUpperCase();

  const copyToClipboard = (text, label) => {
    navigator.clipboard?.writeText(text).then(
      () => showToast(`${label} copied`, 1400),
      () => showToast('Copy failed', 1400)
    );
  };

  const truncateId = (id) => {
    if (!id || id.length <= 12) return id || 'N/A';
    return id.slice(0, 6) + '…' + id.slice(-4);
  };

  // #164 — human-readable role label and gating for the Open Dashboard
  // link. Owner = primary account holder who created the workspace and
  // therefore has billing/settings access → we show them the link into
  // the end-user dashboard. Any other userType ('agent', team members,
  // support staff) is a PWA-only user; the inbox is their whole world
  // and they should not have any path out to a dashboard surface they
  // can't act on anyway.
  const isOwner = session?.userType === 'owner';
  const roleLabel = (() => {
    if (isOwner) return 'Primary account · Admin';
    // Agent roles: prefer the CRM role title when set. Strip any
    // trailing whitespace noise (CRM sample had "master permission or all ").
    const raw = (session?.agentRole || '').toString().trim();
    if (raw) {
      // Capitalize first letter of each word for display.
      return raw
        .split(/\s+/)
        .map(w => w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w)
        .join(' ');
    }
    return 'Team member';
  })();

  return html`
    <div class="inbox-screen inbox-screen-profile" style="display:flex">
      <div class="inbox-header">
        <button class="inbox-header-icon-btn" onClick=${() => navigate('chat-list')} title="Back">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="m15 18-6-6 6-6"/>
          </svg>
        </button>
        <h2 class="inbox-header-title">Profile</h2>
        <button
          class="inbox-header-icon-btn"
          onClick=${() => navigate(SCREENS.SETTINGS)}
          title="Settings"
          style="margin-left:auto"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
        </button>
      </div>

      <div class="inbox-profile-content">
        <div class="inbox-profile-card">
          <div class="inbox-profile-avatar">${initials}</div>
          <h3 class="inbox-profile-name">${session?.agentName || 'Agent'}</h3>
          <p class="inbox-profile-email">${session?.agentEmail || ''}</p>
          <div class="inbox-profile-role">
            <!-- #164 — prominent role chip. Owner gets a brand-green
                 pill so the primary account holder sees "you're the
                 admin" at a glance; team members get a muted blue
                 pill with their CRM role title (or "Team member"). -->
            <span
              class="inbox-role-chip"
              title=${isOwner ? 'Primary account holder — full admin access' : `Team member role: ${roleLabel}`}
              style=${isOwner
                ? 'display:inline-flex;align-items:center;gap:6px;background:rgba(37,211,102,0.16);color:#25D366;padding:4px 11px;border-radius:999px;font-size:0.72rem;font-weight:600;letter-spacing:0.02em;border:1px solid rgba(37,211,102,0.35)'
                : 'display:inline-flex;align-items:center;gap:6px;background:rgba(96,165,250,0.14);color:#93c5fd;padding:4px 11px;border-radius:999px;font-size:0.72rem;font-weight:600;letter-spacing:0.02em;border:1px solid rgba(96,165,250,0.32)'
              }
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
                ${isOwner
                  ? html`<path d="M12 2l3 6 6 1-4.5 4 1 6-5.5-3-5.5 3 1-6L3 9l6-1z"/>`
                  : html`
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    `
                }
              </svg>
              ${roleLabel}
            </span>
          </div>
        </div>

        <div class="inbox-profile-card" style="align-items:stretch;text-align:left">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
            <span style="font-size:0.72rem;text-transform:uppercase;letter-spacing:0.06em;color:var(--text-muted,#7d8799);font-weight:600">Account ID</span>
            <button
              class="inbox-copy-chip"
              onClick=${() => copyToClipboard(session?.accountId, 'Account ID')}
              title=${session?.accountId || ''}
            >
              ${truncateId(session?.accountId)}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
          </div>
          ${session?.wabaId && html`
            <div style="display:flex;justify-content:space-between;align-items:center">
              <span style="font-size:0.72rem;text-transform:uppercase;letter-spacing:0.06em;color:var(--text-muted,#7d8799);font-weight:600">WABA ID</span>
              <button
                class="inbox-copy-chip"
                onClick=${() => copyToClipboard(session?.wabaId, 'WABA ID')}
                title=${session?.wabaId || ''}
              >
                ${truncateId(session?.wabaId)}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
              </button>
            </div>
          `}
        </div>

        <!-- Inbox 9.3 — Channels card. Available to every user_type so
             a team member who can't see a newly added channel can self-
             recover without escalating to the owner. Auto-refresh on /me
             handles the silent path; this button is the override for
             "I added it just now and need it visible now". -->
        <div class="inbox-profile-card" style="align-items:stretch;text-align:left">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:${(session?.channels?.length || 0) > 0 ? '10px' : '0'}">
            <span style="font-size:0.72rem;text-transform:uppercase;letter-spacing:0.06em;color:var(--text-muted,#7d8799);font-weight:600">
              Channels${session?.channels?.length ? ` · ${session.channels.length}` : ''}
            </span>
            <button
              onClick=${handleRefreshChannels}
              disabled=${refreshingChannels}
              class="inbox-profile-update-btn"
              title="Pull the latest channels from Partner Panel"
              aria-label="Refresh channels"
              style="display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:50%;border:1px solid rgba(255,255,255,0.12);background:rgba(255,255,255,0.04);color:inherit;cursor:pointer;transition:all 0.15s ease"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"
                style=${refreshingChannels ? 'animation:inbox-spin 1s linear infinite' : ''}>
                <polyline points="23 4 23 10 17 10"/>
                <polyline points="1 20 1 14 7 14"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
                <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/>
              </svg>
            </button>
          </div>
          ${(session?.channels || []).map(ch => html`
            <div style="display:flex;justify-content:space-between;align-items:center;padding:4px 0;font-size:0.78rem;color:var(--text-secondary)">
              <span style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${ch.display_name || ch.phone_number_id}</span>
              <span style="font-size:0.7rem;color:var(--text-muted,#7d8799);font-family:ui-monospace, SFMono-Regular, Menlo, monospace">${truncateId(ch.phone_number_id)}</span>
            </div>
          `)}
          ${!(session?.channels || []).length && html`
            <div style="font-size:0.74rem;color:var(--text-muted,#7d8799);padding:4px 0">No active WhatsApp channels.</div>
          `}
        </div>

        ${!isInstalled && canInstall && html`
          <!-- #188 — Cached beforeinstallprompt available. Single tap fires
               the OS install dialog. Dismiss button hides the row for
               this device until they reset localStorage. -->
          <div class="inbox-profile-card" style="font-size:0.85rem;color:var(--text-secondary);background:rgba(37,211,102,0.08);border:1px solid rgba(37,211,102,0.22)">
            <div style="display:flex;align-items:center;gap:10px">
              <div style="flex:1;min-width:0">
                <div style="font-weight:600;color:var(--text-primary);margin-bottom:2px">Install ODIV Inbox</div>
                <div style="font-size:0.74rem;color:var(--text-secondary)">Get push notifications + faster startup. One-tap install.</div>
              </div>
              <button
                onClick=${handleInstall}
                disabled=${installing}
                class="inbox-btn"
                style="padding:6px 14px;font-size:0.78rem;background:#25D366;color:#0a1f0e;font-weight:600;border:none"
              >
                ${installing ? '…' : 'Install'}
              </button>
              <button
                onClick=${dismissInstallCta}
                class="inbox-btn-link"
                style="padding:4px 6px;font-size:1rem;color:var(--text-muted);background:transparent;border:none"
                title="Don't show this again"
                aria-label="Dismiss install CTA"
              >×</button>
            </div>
          </div>
        `}

        <!-- #164 — Open Dashboard only for primary account holders.
             Team members and agents are PWA-only; the inbox is their
             entire workspace and they have nothing actionable on the
             end-user dashboard anyway. Owners keep the link. -->
        ${isOwner && html`
          <a href="/dashboard.html" class="inbox-btn inbox-btn-ghost" style="text-decoration:none;margin-bottom:0.5rem">
            Open full dashboard →
          </a>
        `}

        <button class="inbox-logout-btn" onClick=${handleLogout}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Sign out
        </button>

        <div class="inbox-profile-version" style="margin-top:1rem;text-align:center;font-size:0.72rem;color:var(--text-muted, #7d8799);font-family:ui-monospace, SFMono-Regular, Menlo, monospace;display:flex;align-items:center;justify-content:center;gap:8px">
          <span>
            ODIV Inbox · ${BUILD_VERSION} ·
            ${swStatus === 'active'   && 'service worker active'}
            ${swStatus === 'updating' && 'service worker updating…'}
            ${swStatus === 'off'      && 'no service worker'}
            ${swStatus === 'checking' && 'checking…'}
          </span>
          <button
            onClick=${handleUpdate}
            disabled=${updating}
            class="inbox-profile-update-btn"
            title="Pull the latest build and reload"
            aria-label="Check for updates"
            style="display:inline-flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:50%;border:1px solid rgba(255,255,255,0.12);background:rgba(255,255,255,0.04);color:inherit;cursor:pointer;transition:all 0.15s ease"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"
              style=${updating ? 'animation:inbox-spin 1s linear infinite' : ''}>
              <polyline points="23 4 23 10 17 10"/>
              <polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
              <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  `;
}
